import { useState, useEffect } from "react";
import API from "@/lib/api";
import { formatCurrency, formatPercent } from "@/lib/formatters";
import MetricCard from "@/components/shared/MetricCard";
import DataTable from "@/components/shared/DataTable";
import USMapGrid from "@/components/shared/USMapGrid";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { DollarSign, Building2, Landmark, TrendingUp, BarChart3 } from "lucide-react";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip as ReTooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
} from "recharts";

const CHART_COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#06B6D4", "#F97316"];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#151B24] border border-[#2D3748] rounded-sm p-3 text-sm shadow-xl">
      <p className="font-semibold text-foreground mb-1">{label || payload[0]?.name}</p>
      {payload.map((p, i) => (
        <p key={i} className="font-data text-xs" style={{ color: p.color || p.fill }}>
          {p.dataKey || p.name}: {typeof p.value === "number" && p.value > 10000 ? formatCurrency(p.value) : p.value}
          {p.payload?.percentage ? ` (${p.payload.percentage}%)` : ""}
        </p>
      ))}
    </div>
  );
};

export default function ExecutiveDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get("/dashboard/summary")
      .then((res) => setData(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="p-6 space-y-6" data-testid="dashboard-loading">
        <div className="grid grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-28" />)}
        </div>
        <div className="grid grid-cols-12 gap-4">
          <Skeleton className="col-span-5 h-80" />
          <Skeleton className="col-span-7 h-80" />
        </div>
      </div>
    );
  }

  const s = data?.summary || {};
  const sectors = data?.sector_allocation || [];
  const geo = data?.geographic_exposure || [];
  const benchmarks = data?.benchmark_comparison || [];

  const fundCols = [
    { key: "name", label: "Fund", sortable: false },
    { key: "manager", label: "Manager" },
    { key: "nav", label: "NAV", align: "right", render: (v) => formatCurrency(v) },
    { key: "irr", label: "IRR", align: "right", render: (v) => <span className="text-emerald-500">{v}%</span> },
  ];

  const propCols = [
    { key: "name", label: "Property", sortable: false },
    { key: "city", label: "City" },
    { key: "sector", label: "Sector", render: (v) => <Badge variant="secondary" className="font-data text-xs">{v}</Badge> },
    { key: "value", label: "Value", align: "right", render: (v) => formatCurrency(v) },
    { key: "occupancy_rate", label: "Occupancy", align: "right", render: (v) => `${v}%` },
  ];

  return (
    <div className="p-6 space-y-6" data-testid="executive-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Executive Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">Real-time portfolio intelligence overview</p>
        </div>
        <div className="text-xs text-muted-foreground font-data">
          Last updated: {new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
        </div>
      </div>

      <div className="grid grid-cols-5 gap-4 animate-fade-in">
        <MetricCard label="Portfolio Value" value={formatCurrency(s.total_value)} change={s.year_value_change} icon={DollarSign} />
        <MetricCard label="Total Properties" value={s.total_properties} change={4.2} suffix="" icon={Building2} />
        <MetricCard label="Total Funds" value={s.total_funds} change={2.1} suffix="" icon={Landmark} />
        <MetricCard label="Average IRR" value={formatPercent(s.average_irr)} change={s.year_irr_change} icon={TrendingUp} />
        <MetricCard label="Total NOI" value={formatCurrency(s.total_noi)} change={s.year_noi_change} icon={BarChart3} />
      </div>

      <div className="grid grid-cols-12 gap-4">
        <Card className="col-span-5 border border-border bg-card">
          <CardHeader className="border-b border-border p-3">
            <CardTitle className="text-sm font-semibold">Sector Allocation</CardTitle>
          </CardHeader>
          <CardContent className="p-4" data-testid="sector-pie-chart">
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={sectors}
                  dataKey="allocation_pct"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  stroke="none"
                >
                  {sectors.map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <ReTooltip content={<CustomTooltip />} />
                <Legend
                  verticalAlign="bottom"
                  formatter={(value) => <span className="text-xs text-muted-foreground">{value}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="col-span-7 border border-border bg-card">
          <CardHeader className="border-b border-border p-3">
            <CardTitle className="text-sm font-semibold">Geographic Exposure</CardTitle>
          </CardHeader>
          <CardContent className="p-4" data-testid="geographic-map">
            <USMapGrid data={geo} />
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-12 gap-4">
        <Card className="col-span-6 border border-border bg-card">
          <CardHeader className="border-b border-border p-3">
            <CardTitle className="text-sm font-semibold">Top Funds by NAV</CardTitle>
          </CardHeader>
          <CardContent className="p-0" data-testid="top-funds-table">
            <DataTable columns={fundCols} data={data?.top_funds || []} />
          </CardContent>
        </Card>

        <Card className="col-span-6 border border-border bg-card">
          <CardHeader className="border-b border-border p-3">
            <CardTitle className="text-sm font-semibold">Top Properties by Value</CardTitle>
          </CardHeader>
          <CardContent className="p-0" data-testid="top-properties-table">
            <DataTable columns={propCols} data={data?.top_properties || []} />
          </CardContent>
        </Card>
      </div>

      <Card className="border border-border bg-card">
        <CardHeader className="border-b border-border p-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold">Portfolio vs NCREIF Benchmark</CardTitle>
            <Badge variant="secondary" className="font-data text-xs">2024</Badge>
          </div>
        </CardHeader>
        <CardContent className="p-4" data-testid="benchmark-chart">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={benchmarks} barGap={4} barCategoryGap="20%">
              <CartesianGrid strokeDasharray="4 4" stroke="#2D3748" vertical={false} />
              <XAxis dataKey="metric" tick={{ fill: "#9CA3AF", fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#9CA3AF", fontSize: 12 }} axisLine={false} tickLine={false} />
              <ReTooltip content={<CustomTooltip />} />
              <Legend formatter={(value) => <span className="text-xs text-muted-foreground">{value}</span>} />
              <Bar dataKey="portfolio_value" name="Portfolio" fill="#3B82F6" radius={[2, 2, 0, 0]} />
              <Bar dataKey="benchmark_value" name="NCREIF" fill="#6366F1" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}
